/* global document, CustomEvent */

const { computed } = require('mobx');
const { inject, observer } = require('mobx-react');
const { Link } = require('react-router-dom');
const React = require('react');

const { ClickTracker } = require('bernie-analytics');
const { ErrorBoundary, Layer, Viewport, ViewSmall, ViewMedium, ViewLarge } = require('bernie-view');
const { QueryRoute } = require('bernie-core');
const { withLocalization } = require('bernie-l10n');

const { liveAnnounce, classNames } = require('uitk-react-utils');

const { SearchResultsStore } = require('stores/search-results-store');

const { PageName } = require('constants/pages');

const { AlternateDestinations } = require('components/search/alternate-destinations');
const { DestinationRefinement } = require('components/search/destination-refinement');
const { ChangeSearchDialog } = require('components/search/change-search-dialog');
const { LoyaltyEducationDialog } = require('components/common/loyalty-education/loyalty-education-dialog');
const { MainRegion } = require('components/layout/pages/main-region');
const { MasterPage } = require('components/layout/pages/master-page');
const { MesoFooter } = require('components/meso/meso-footer');
const { MesoIntentMediaExp } = require('components/meso/meso-intent-media-exp');
const { ClickTripz } = require('components/meso/internals/click-tripz');
const { MesoSideRail } = require('components/meso/meso-side-rail');
const { Overlay } = require('components/layout/layers/overlay');
const { Playback } = require('components/search/playback/playback');
const { Results } = require('components/search/results');
const { RewardsEducationDialog } = require('components/common/rewards-education-dialog');
const { Sidebar } = require('components/search/sidebar.tsx');
const { SideRailRegion } = require('components/layout/pages/side-rail-region');
const { ToolbarRegion } = require('components/layout/pages/toolbar-region');
const { Tools } = require('components/search/tools.tsx');
const { ExitIntent } = require('components/search/exit-intent');

const ViewportListener = require('components/common/lazy/viewport-listener.js');

const { UitkSecondaryButton } = require('uitk-react-button');

const ButtonTracked = ClickTracker(UitkSecondaryButton);
const MasterPageSideRailTypes = MasterPage.wrappedComponent.sideRailTypes;
@withLocalization
@inject(
  'searchResults',
  'page',
  'layerManager',
  'context',
  'uiPage',
  'analytics',
  'routeStore',
  'shortlist',
  'experiment',
  'customerInterestsStore',
  'multiItem'
)
@observer
class Search extends React.Component {
  resultRefs = [];
  renderRemainingAfterIndex = 9;

  constructor(props) {
    super(props);
    this.handleOnDismiss = this.handleOnDismiss.bind(this);
    this.setResultRef = this.setResultRef.bind(this);
    this.handleShowMore = this.handleShowMore.bind(this);
    this.prevLoading = false;
    this.state = {
      jsEnabled: false
    };
  }

  @computed.struct
  get omnitureData() {
    if (this.props.searchResults && this.props.analytics) {
      const resultsOmnitureData = this.props.searchResults.omnitureData || null;

      if (resultsOmnitureData) {
        // use mergeOmnitureData even with one source to ensure the data is properly formatted and de-referenced
        const mergedData = this.props.analytics.mergeOmnitureData(resultsOmnitureData);

        return mergedData;
      }
    }

    return null;
  }

  @computed.struct
  get universalDataObject() {
    if (this.props.searchResults && typeof this.props.searchResults.universalDataObject === 'object') {
      return this.props.searchResults.universalDataObject;
    }

    return {};
  }

  get backToChangeSearchQuery() {
    const { routeStore } = this.props;
    if (routeStore && typeof routeStore.mergeQueryString === 'function') {
      return routeStore.mergeQueryString(routeStore.search, { pwaOverlay: 'changeSearch' });
    }
  }

  get loading() {
    const { searchResults, uiPage } = this.props;

    return searchResults.isLoading && !uiPage.displayProgressIndicator;
  }

  componentDidMount() {
    this.props.analytics.markPageUsable();
    this.announceLoadingState();
    this.setState({
      jsEnabled: true
    });
  }

  componentDidUpdate() {
    this.announceLoadingState();
    this.markPageUsableOnUpdate();
  }

  markPageUsableOnUpdate() {
    const { isLoading } = this.props.searchResults;
    if (isLoading === false && this.prevLoading === true) {
      this.props.analytics.markPageUsable();
    }
    this.prevLoading = isLoading;
  }

  announceLoadingState() {
    const { isLoading, isError, isEmpty } = this.props.searchResults;
    const { l10n } = this.props;

    let message;
    if (isLoading) {
      message = l10n.formatText('hotels.searchResults.resultsLoading.liveAnnounce');
    } else if (isError) {
      message = l10n.formatText('hotels.searchResults.errorRetrievingResults.liveAnnounce');
    } else if (isEmpty) {
      message = l10n.formatText('hotels.searchResults.emptyResults.liveAnnounce');
    } else {
      message = l10n.formatText('hotels.searchResults.resultsUpdated.liveAnnounce');
    }
    if (message !== this.lastLoadingStateMessage) {
      liveAnnounce(message, 'assertive');
      this.lastLoadingStateMessage = message;
    }
  }

  handleOnDismiss() {
    const { routeStore } = this.props;
    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
  }

  setResultRef(ref, index) {
    this.resultRefs[index] = ref;

    if (index === this.renderRemainingAfterIndex && ref !== null) {
      ViewportListener.subscribe(
        'window',
        ref,
        {
          didEnter: () => this.props.searchResults.setHotelsMaxDisplayToTotalRenderCount()
        },
        { extendBoundingBoxToBottomOfPage: true, triggerOnce: true }
      );
    }
  }

  render() {
    const { page, l10n, searchResults } = this.props;
    const destinationPresent = searchResults && searchResults.searchCriteria && searchResults.searchCriteria.regionId;

    const destinationTitle = this.resolveDestination();

    page.title = destinationTitle
      ? l10n.formatText('hotels.searchResults.successfulSearch.pageTitle', destinationTitle)
      : l10n.formatText('hotels.searchResults.invalidSearch.pageTitle');

    const rewardsEducation = searchResults && searchResults.rewardsDialogContent && searchResults.rewardsDialogContent.rewardsEducation;
    const loyaltyEducation = searchResults && searchResults.loyaltyEducation;
    const clickTripzData = {
      referrerInfo: searchResults.referrerInfo,
      sharedGuids: searchResults.sharedGuids,
      regionInfo: searchResults.regionInfo,
      searchCriteria: searchResults.searchCriteria
    };

    const resultsClasses = classNames({
      'uitk-grid': destinationPresent
    });

    return (
      <MasterPage
        className="search-results"
        loading={this.loading}
        route={this.props.route}
        searchCriteria={searchResults.searchCriteria}
        sideRailType={MasterPageSideRailTypes.NEXT_TO_MAIN_AND_TOOLBAR}
      >
        <ToolbarRegion className="search-results-toolbar">
          <Playback />
        </ToolbarRegion>
        <MainRegion className="search-results__main all-x-padding-two">
          <div className="search-results-listing all-y-padding-three">
            <Tools />
            <Viewport>
              <ViewSmall>{this.renderResults()}</ViewSmall>
              <ViewMedium>{this.renderResults(1)}</ViewMedium>
              <ViewLarge>
                <div className={resultsClasses}>
                  <div className="uitk-cell all-cell-1-4">
                    <Sidebar isActive={this.state.jsEnabled} />
                  </div>
                  <div className="uitk-cell all-cell-3-4 all-x-gutter-six">{this.renderResults(1)}</div>
                </div>
              </ViewLarge>
            </Viewport>
          </div>
          <MesoIntentMediaExp pageName={PageName.SEARCH} searchCriteria={searchResults.searchCriteria} />
          <ErrorBoundary label="hotel.search.clicktripz">
            <ClickTripz {...clickTripzData} />
          </ErrorBoundary>
          <QueryRoute query={{ pwaDialog: 'rewardsEducation' }}>
            <RewardsEducationDialog rewardsEducation={rewardsEducation} />
          </QueryRoute>
          <QueryRoute query={{ pwaDialog: 'loyaltyEducation' }}>
            <LoyaltyEducationDialog loyaltyEducation={loyaltyEducation} />
          </QueryRoute>
          {searchResults.exitIntent && <ExitIntent exitIntent={searchResults.exitIntent} />}
        </MainRegion>
        <SideRailRegion>
          <MesoSideRail pageName={PageName.SEARCH} />
        </SideRailRegion>
      </MasterPage>
    );
  }

  renderResults(largeGridColumnCount = 3) {
    const {
      hotels,
      hotelsMaxDisplay,
      insights,
      isLoading,
      maxHotelsDisplayedPerLoad,
      messages,
      searchCriteria,
      alternateDestinations,
      splitListings,
      displayedUnavailableCount,
      destinationRefinement
    } = this.props.searchResults;
    const { endDate, startDate } = searchCriteria;
    const { l10n, routeStore, analytics } = this.props;
    const { hotelListings, messageListings } = splitListings(hotels, messages);
    const allHotelsDisplayed = hotels ? hotelsMaxDisplay >= hotelListings.length : true;
    const disableShowMore = isLoading || allHotelsDisplayed || !this.state.jsEnabled;
    const pageLoadNumber = hotelsMaxDisplay / maxHotelsDisplayedPerLoad;
    const loadMoreRfrr = `:LoadMore${pageLoadNumber}`;
    const propertyAlternateDestinations = alternateDestinations && alternateDestinations.propertyAlternateDestinations;
    const displayAlternateDestinations = displayedUnavailableCount < SearchResultsStore.ALTERNATE_DESTINATIONS_UNAVAILABLE_INDEX;
    const propertyDestinationRefinement =
      destinationRefinement &&
      destinationRefinement.propertySearchDestinations &&
      destinationRefinement.propertySearchDestinations.destinationRefinement;
    const resultsClassName = `uitk-cell xl-cell-1-${largeGridColumnCount} l-cell-1-${largeGridColumnCount} m-cell-1-1 s-cell-1-1`;
    const buttonProps = {
      rfrr: loadMoreRfrr,
      linkName: 'Show more results',
      disabled: disableShowMore,
      text: l10n.formatText('hotels.searchResults.paginationButton.label')
    };

    return (
      <React.Fragment>
        {/* TODO: Move show more button into hotel list */}
        {!this.loading &&
          propertyDestinationRefinement && <DestinationRefinement analytics={analytics} {...propertyDestinationRefinement} />}
        <h2 className="is-visually-hidden">{l10n.formatText('hotels.search.results.heading')}</h2>
        <Results
          className={resultsClassName}
          endDate={endDate}
          hotelListings={hotelListings}
          hotels={hotels}
          insights={insights}
          loading={this.loading}
          messageListings={messageListings}
          messages={messages}
          searchResults={this.props.searchResults}
          setResultRef={this.setResultRef}
          startDate={startDate}
          alternateDestinations={propertyAlternateDestinations}
          routeStore={routeStore}
        />
        <ShowMoreButton allHotelsDisplayed={allHotelsDisplayed} buttonProps={buttonProps} onClickHandler={this.handleShowMore} />
        {this.renderErrorMessage()}
        {!this.loading && displayAlternateDestinations && <AlternateDestinations {...propertyAlternateDestinations} />}
        <MesoFooter pageName={PageName.SEARCH} destination={this.resolveDestination()} />
      </React.Fragment>
    );
  }

  refreshAds() {
    if (document && CustomEvent && typeof document.dispatchEvent === 'function') {
      document.dispatchEvent(new CustomEvent('mesoTriggeredRefresh', { detail: { type: 'impressionRefresh' } }));
    }
  }

  showMoreHotels() {
    const { analytics, context, customerInterestsStore, multiItem, searchResults } = this.props;
    this.props.uiPage.showProgressIndicator(400);

    const multiItemContext = multiItem ? multiItem.multiItemContext : null;
    const customerInterests = customerInterestsStore ? customerInterestsStore.customerInterests : null;

    searchResults.showMoreHotels(analytics, context, customerInterests, multiItemContext).then(previousMax => {
      // Move focus to the first newly revealed HSR cards, for better screen reader experience.
      this.resultRefs[previousMax].focus();
    });
  }

  handleShowMore() {
    this.refreshAds();
    this.showMoreHotels();
  }

  renderErrorMessage() {
    const searchResults = this.props.searchResults || {};
    const { hotels, isError, isEmpty } = searchResults;
    const destination = this.resolveDestination();
    const { l10n } = this.props;

    const hasAvailableHotels = hotels && hotels.length > 0;

    if (isError || isEmpty) {
      return (
        <div className="uitk-type-center all-b-padding-twelve">
          <ErrorContainer errorMessage={`${l10n.formatText('hotels.searchResults.destinationMissing.message')}`} />
          {!destination &&
            hasAvailableHotels && (
              <BackToChangeSearch
                analytics={this.props.analytics}
                l10n={l10n}
                onDismiss={this.handleOnDismiss}
                query={this.backToChangeSearchQuery}
              />
            )}
        </div>
      );
    }

    return null;
  }

  resolveDestination() {
    const { searchedDestination, searchCriteria } = this.props.searchResults;

    return searchCriteria.destination || searchedDestination;
  }
}

const ErrorContainer = ({ errorMessage, children }) => (
  <div className="uitk-type-300 pwa-theme--grey-700 uitk-type-center search-results-error">
    <p>{errorMessage}</p>
    {children}
  </div>
);

const BackToChangeSearch = ({ analytics, onDismiss, query, l10n }) => (
  <div className="backToChangeSearch">
    <ButtonTracked
      linkName="Back to change search"
      rfrr=":destinationMissingChangeSearch"
      tag={Link}
      text={l10n.formatText('header.search.linkText')}
      to={{ search: query }}
    />

    <QueryRoute query={{ pwaOverlay: 'changeSearch' }}>
      <Layer id="destination-missing-changesearch" onDismiss={onDismiss}>
        <Overlay type="takeover" openAnimation="slideup">
          <ChangeSearchDialog analytics={analytics} onDismiss={onDismiss} />
        </Overlay>
      </Layer>
    </QueryRoute>
  </div>
);

const ShowMoreButton = ({ allHotelsDisplayed, buttonProps, onClickHandler }) => {
  if (allHotelsDisplayed) {
    return null;
  }

  return (
    <ErrorBoundary label="hotel.search.showMoreResultsButton">
      <section className="uitk-flex uitk-flex-justify-content-center all-t-padding-three">
        <ButtonTracked {...buttonProps} data-stid="show-more-results" onClick={onClickHandler} />
      </section>
    </ErrorBoundary>
  );
};

module.exports = Search;
